import { useState, useEffect } from 'preact/hooks';
import {
  connectIntegration,
  getBambooHRPreview,
  saveBambooHRMappings,
  executeBambooHRImport
} from '../../../lib/api';

const STEPS = {
  CONNECT: 1,
  USERS: 2,
  LEAVE_TYPES: 3,
  REVIEW: 4,
  IMPORTING: 5,
  COMPLETE: 6
};

const stepLabels = ['Connect', 'Users', 'Leave Types', 'Review'];

export default function BambooHRWizard({ onClose, onComplete, existingConfig }) {
  const [step, setStep] = useState(existingConfig ? STEPS.USERS : STEPS.CONNECT);
  const [apiKey, setApiKey] = useState('');
  const [subdomain, setSubdomain] = useState(existingConfig?.config?.subdomain || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [previewData, setPreviewData] = useState(null);
  const [userMappings, setUserMappings] = useState([]);
  const [leaveTypeMappings, setLeaveTypeMappings] = useState([]);

  const [includeTimeOff, setIncludeTimeOff] = useState(true);
  const [result, setResult] = useState(null);

  // If we have existing config, load preview data
  useEffect(() => {
    if (existingConfig) {
      loadPreviewData();
    }
  }, [existingConfig]);

  const loadPreviewData = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getBambooHRPreview();
      setPreviewData(data);

      // Initialize user mappings
      const userMaps = data.employees.items.map(emp => ({
        externalId: emp.id,
        externalName: emp.name,
        externalEmail: emp.email,
        action: emp.matched ? 'map' : 'skip',
        userId: emp.matchedUserId || ''
      }));
      setUserMappings(userMaps);

      // Initialize leave type mappings
      const ltMaps = data.timeOffTypes.items.map(lt => ({
        externalId: lt.id,
        externalName: lt.name,
        externalUnits: lt.units,
        action: lt.matched ? 'map' : 'skip',
        leaveTypeId: lt.matchedLeaveTypeId || ''
      }));
      setLeaveTypeMappings(ltMaps);

      if (!existingConfig) {
        setStep(STEPS.USERS);
      }
    } catch (err) {
      setError(err.message || 'Failed to load BambooHR data');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your API key');
      return;
    }
    if (!subdomain.trim()) {
      setError('Please enter your BambooHR subdomain');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await connectIntegration('bamboohr', {
        apiKey: apiKey.trim(),
        config: { subdomain: subdomain.trim() }
      });

      // Now load preview data
      await loadPreviewData();
    } catch (err) {
      setError(err.message || 'Failed to connect to BambooHR');
      setLoading(false);
    }
  };

  const handleSaveMappingsAndImport = async () => {
    setStep(STEPS.IMPORTING);
    setError('');

    try {
      // Save mappings first
      await saveBambooHRMappings({
        userMappings: userMappings.map(m => ({
          externalId: m.externalId,
          externalEmail: m.externalEmail,
          externalName: m.externalName,
          userId: m.userId,
          action: m.action
        })),
        leaveTypeMappings: leaveTypeMappings.map(m => ({
          externalId: m.externalId,
          externalName: m.externalName,
          leaveTypeId: m.leaveTypeId,
          action: m.action
        }))
      });

      // Execute import
      const importResult = await executeBambooHRImport();
      setResult(importResult);
      setStep(STEPS.COMPLETE);
    } catch (err) {
      setError(err.message || 'Import failed');
      setStep(STEPS.REVIEW);
    }
  };

  const handleClose = () => {
    if (step === STEPS.COMPLETE && onComplete) {
      onComplete();
    }
    onClose();
  };

  const updateUserMapping = (index, field, value) => {
    setUserMappings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateLeaveTypeMapping = (index, field, value) => {
    setLeaveTypeMappings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const currentStepIndex = Math.min(step, 4);

  // Styles
  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    },
    backdrop: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.95) 0%, rgba(16, 185, 129, 0.95) 100%)',
      backdropFilter: 'blur(8px)'
    },
    container: {
      position: 'relative',
      width: '100%',
      maxWidth: '680px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '16px 16px 0 0',
      padding: '20px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    headerIcon: {
      width: '44px',
      height: '44px',
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px'
    },
    card: {
      background: 'white',
      borderRadius: '0 0 16px 16px',
      boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
      overflow: 'hidden',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(90vh - 140px)'
    },
    progressBar: {
      background: 'rgba(255,255,255,0.1)',
      padding: '16px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      flexWrap: 'wrap'
    },
    stepPill: (active, completed) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      borderRadius: '20px',
      background: active ? 'white' : completed ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
      color: active ? '#059669' : 'white',
      fontSize: '13px',
      fontWeight: '500',
      transition: 'all 0.2s'
    }),
    button: (primary, disabled) => ({
      padding: primary ? '12px 28px' : '10px 20px',
      background: disabled ? '#d1d5db' : primary ? 'linear-gradient(135deg, #10b981, #059669)' : '#f3f4f6',
      color: primary ? 'white' : '#374151',
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s'
    }),
    input: {
      width: '100%',
      padding: '14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 0.2s'
    },
    select: {
      padding: '8px 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '13px',
      background: 'white',
      cursor: 'pointer'
    },
    error: {
      background: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '10px',
      padding: '12px 16px',
      marginBottom: '20px',
      color: '#dc2626',
      fontSize: '14px'
    },
    scrollArea: {
      padding: '16px 24px',
      overflowY: 'auto',
      flex: 1
    }
  };

  return (
    <div style={styles.overlay}>
      {/* Gradient Backdrop */}
      <div style={styles.backdrop} onClick={step !== STEPS.IMPORTING ? handleClose : undefined} />

      {/* Popup Container */}
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={styles.headerIcon}>
              <span role="img" aria-label="bamboo">🎋</span>
            </div>
            <div>
              <h1 style={{ margin: 0, color: 'white', fontSize: '18px', fontWeight: '600' }}>BambooHR Integration</h1>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Import employees and time-off data</p>
            </div>
          </div>
          {step !== STEPS.IMPORTING && (
            <button
              onClick={handleClose}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: '8px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Progress Steps */}
        {step <= STEPS.REVIEW && (
          <div style={styles.progressBar}>
            {stepLabels.map((label, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={styles.stepPill(idx + 1 === currentStepIndex, idx + 1 < currentStepIndex)}>
                  {idx + 1 < currentStepIndex ? (
                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span style={{ fontSize: '12px', fontWeight: '600' }}>{idx + 1}</span>
                  )}
                  <span>{label}</span>
                </div>
                {idx < stepLabels.length - 1 && (
                  <div style={{ width: '16px', height: '2px', background: idx + 1 < currentStepIndex ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Main Content Card */}
        <div style={styles.card}>

          {/* Step 1: Connect */}
          {step === STEPS.CONNECT && (
            <div style={{ padding: '36px 32px' }}>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <div style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #34d399, #10b981)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '28px' }}>
                  <span role="img" aria-label="key">🔑</span>
                </div>
                <h2 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: '600', color: '#1f2937' }}>Connect to BambooHR</h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Enter your BambooHR API key and subdomain</p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Subdomain</label>
                <input
                  type="text"
                  value={subdomain}
                  onInput={(e) => setSubdomain(e.target.value)}
                  placeholder="yourcompany"
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#34d399'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                  From yourcompany.bamboohr.com
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>API Key</label>
                <input
                  type="password"
                  value={apiKey}
                  onInput={(e) => setApiKey(e.target.value)}
                  placeholder="Paste your API key here..."
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#34d399'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div style={{ background: '#f3f4f6', borderRadius: '12px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', background: '#d1fae5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="16" height="16" fill="none" stroke="#059669" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>How to get your API key</p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>BambooHR → Settings → API Keys → Add New Key</p>
                  </div>
                </div>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button onClick={handleConnect} disabled={loading} style={styles.button(true, loading)}>
                {loading ? 'Connecting...' : 'Connect & Continue'}
              </button>
            </div>
          )}

          {/* Step 2: Users */}
          {step === STEPS.USERS && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '600', color: '#1f2937' }}>Map Users</h2>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                      {previewData?.employees?.active || 0} active employees found
                    </p>
                  </div>
                </div>
              </div>
              <div style={styles.scrollArea}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>Loading...</div>
                ) : userMappings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>No employees found</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {userMappings.map((user, idx) => (
                      <div key={user.externalId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '10px' }}>
                        <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #34d399, #10b981)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: '600', flexShrink: 0 }}>
                          {user.externalName?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.externalName}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.externalEmail}</p>
                        </div>
                        <select
                          value={user.action}
                          onChange={(e) => updateUserMapping(idx, 'action', e.target.value)}
                          style={styles.select}
                        >
                          <option value="map">Map to existing</option>
                          <option value="skip">Skip</option>
                        </select>
                        {user.action === 'map' && (
                          <select
                            value={user.userId}
                            onChange={(e) => updateUserMapping(idx, 'userId', e.target.value)}
                            style={{ ...styles.select, maxWidth: '140px' }}
                          >
                            <option value="">Select...</option>
                            {previewData?.employees?.items?.find(e => e.id === user.externalId)?.matchedUserId && (
                              <option value={previewData.employees.items.find(e => e.id === user.externalId).matchedUserId}>
                                {previewData.employees.items.find(e => e.id === user.externalId).matchedUserName} (auto)
                              </option>
                            )}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(STEPS.CONNECT)} style={styles.button(false, false)}>Back</button>
                <button onClick={() => setStep(STEPS.LEAVE_TYPES)} style={styles.button(true, false)}>Continue</button>
              </div>
            </div>
          )}

          {/* Step 3: Leave Types */}
          {step === STEPS.LEAVE_TYPES && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '600', color: '#1f2937' }}>Map Leave Types</h2>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                      {previewData?.timeOffTypes?.total || 0} time-off types found
                    </p>
                  </div>
                </div>
              </div>
              <div style={styles.scrollArea}>
                {leaveTypeMappings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '36px', color: '#9ca3af' }}>No leave types found</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {leaveTypeMappings.map((lt, idx) => (
                      <div key={lt.externalId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '10px' }}>
                        <div style={{ width: '36px', height: '36px', background: '#10b981', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{lt.externalName}</p>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: '#d1fae5', color: '#059669' }}>
                            {lt.externalUnits}
                          </span>
                        </div>
                        <select
                          value={lt.action}
                          onChange={(e) => updateLeaveTypeMapping(idx, 'action', e.target.value)}
                          style={styles.select}
                        >
                          <option value="map">Map to existing</option>
                          <option value="skip">Skip</option>
                        </select>
                        {lt.action === 'map' && (
                          <select
                            value={lt.leaveTypeId}
                            onChange={(e) => updateLeaveTypeMapping(idx, 'leaveTypeId', e.target.value)}
                            style={{ ...styles.select, maxWidth: '150px' }}
                          >
                            <option value="">Select...</option>
                            {previewData?.timeOffTypes?.items?.find(t => t.id === lt.externalId)?.matchedLeaveTypeId && (
                              <option value={previewData.timeOffTypes.items.find(t => t.id === lt.externalId).matchedLeaveTypeId}>
                                {previewData.timeOffTypes.items.find(t => t.id === lt.externalId).matchedLeaveTypeName} (auto)
                              </option>
                            )}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(STEPS.USERS)} style={styles.button(false, false)}>Back</button>
                <button onClick={() => setStep(STEPS.REVIEW)} style={styles.button(true, false)}>Review</button>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === STEPS.REVIEW && (
            <div style={{ padding: '28px 32px' }}>
              <h2 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: '600', color: '#1f2937', textAlign: 'center' }}>Review Your Import</h2>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[
                  {
                    label: 'Users',
                    mapped: userMappings.filter(m => m.action === 'map' && m.userId).length,
                    skipped: userMappings.filter(m => m.action === 'skip').length
                  },
                  {
                    label: 'Leave Types',
                    mapped: leaveTypeMappings.filter(m => m.action === 'map' && m.leaveTypeId).length,
                    skipped: leaveTypeMappings.filter(m => m.action === 'skip').length
                  },
                  {
                    label: 'Time-Off Requests',
                    enabled: includeTimeOff,
                    count: previewData?.timeOffRequests?.approved || 0
                  }
                ].map((item, idx) => (
                  <div key={idx} style={{ background: '#f5f3ff', border: '2px solid #c4b5fd', borderRadius: '12px', padding: '14px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>{item.label}</p>
                    {item.count !== undefined ? (
                      <span style={{ fontSize: '12px', color: '#059669' }}>{item.count} to import</span>
                    ) : (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        <span style={{ color: '#2563eb' }}>{item.mapped} mapped</span>
                        {' • '}
                        <span style={{ color: '#9ca3af' }}>{item.skipped} skipped</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', marginBottom: '20px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeTimeOff}
                  onChange={(e) => setIncludeTimeOff(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#10b981' }}
                />
                <span style={{ fontSize: '13px', color: '#92400e' }}>
                  Import {previewData?.timeOffRequests?.approved || 0} approved time-off requests
                </span>
              </label>

              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <svg width="20" height="20" fill="none" stroke="#059669" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                    <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p style={{ margin: 0, fontSize: '13px', color: '#065f46' }}>
                    Time-off requests will be imported as approved leave and balances will be updated automatically.
                  </p>
                </div>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={() => setStep(STEPS.LEAVE_TYPES)} style={styles.button(false, false)}>Back</button>
                <button onClick={handleSaveMappingsAndImport} style={{ ...styles.button(true, false), background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                  Start Import
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Importing */}
          {step === STEPS.IMPORTING && (
            <div style={{ padding: '60px 40px', textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', margin: '0 auto 24px', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, border: '4px solid #e5e7eb', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', inset: 0, border: '4px solid #10b981', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>Importing your data...</h2>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>This may take a few moments</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Step 6: Complete */}
          {step === STEPS.COMPLETE && result && (
            <div style={{ padding: '40px 36px', textAlign: 'center' }}>
              <div style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)' }}>
                <svg width="32" height="32" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>Import Complete!</h2>
              <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '14px' }}>Your BambooHR data has been imported</p>

              <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px', textAlign: 'left', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>Records Synced</span>
                  <span style={{ color: '#059669', fontSize: '13px', fontWeight: '600' }}>{result.recordsSynced || 0}</span>
                </div>
                {result.details?.employees && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>Employees Updated</span>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>{result.details.employees.updated || 0}</span>
                  </div>
                )}
                {result.details?.timeOff && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ color: '#374151', fontWeight: '500', fontSize: '14px' }}>Time-Off Imported</span>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>{result.details.timeOff.imported || 0}</span>
                  </div>
                )}
              </div>

              {result.errors && result.errors.length > 0 && (
                <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '12px', textAlign: 'left', marginBottom: '16px', fontSize: '12px', color: '#dc2626' }}>
                  <div style={{ fontWeight: '600', marginBottom: '6px' }}>Warnings:</div>
                  {result.errors.slice(0, 3).map((err, i) => (
                    <div key={i}>• {err}</div>
                  ))}
                  {result.errors.length > 3 && <div>...and {result.errors.length - 3} more</div>}
                </div>
              )}

              <button onClick={handleClose} style={styles.button(true, false)}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
